import Factory from "@/factories/Factory.ts";
import { LedgerFactory, type LedgerResource } from "@/factories/LedgerFactory.ts";
import { useAccount } from "@/stores/account.ts";
import type { Currency, Group, Tax } from "@/types/transaction";
import Expense from "@/classes/Expense.ts";
import Income from "@/classes/Income.ts";
import type Transaction from "@/classes/Transaction.ts";
import { RRule } from "rrule";
import date from "@/libs/date";

interface TransactionResource {
  amount: number;
  apply_to_income_tax?: boolean;
  currency: string;
  date: string;
  description: string;
  group: Group | null;
  id: number;
  is_disabled: boolean;
  ledger: LedgerResource | null;
  name: string;
  recurrence: string | null;
  settles_reservation?: string | null;
  tax: Tax | null;
  type: string;
}

class TransactionFactory extends Factory<Transaction, TransactionResource> {
  public fromJson(resource: TransactionResource): Transaction {
    const params = {
      amount: resource.amount,
      applyToIncomeTax: resource.apply_to_income_tax ?? false,
      currency: resource.currency as Currency,
      // The API sends UTC midnight for a calendar date; keep it a calendar date here too.
      date: date(resource.date).startOf("day"),
      description: resource.description,
      group: resource.group,
      id: resource.id,
      isDisabled: resource.is_disabled,
      // A resource can come back without a ledger; give it the account default rather than
      // handing a ledgerless transaction to the rest of the app.
      ledger: resource.ledger
        ? new LedgerFactory().fromJson(resource.ledger)
        : useAccount().defaultLedger,
      name: resource.name,
      recurrence: resource.recurrence !== null ? RRule.fromString(resource.recurrence) : null,
      settlesReservation: resource.settles_reservation ?? null,
      tax: resource.tax,
    };

    return resource.type === "Expense" ? new Expense(params) : new Income(params);
  }
}

export { TransactionFactory, type TransactionResource };
